import React from 'react'

/* -------------------------------------------------------------------------
 * Which tooltip trigger children need a wrapper element — and, far more
 * importantly, which must NOT get one.
 *
 * WHY A WRAPPER IS EVER NEEDED. The shadcn Tooltip binds its trigger with
 * Radix `asChild`: the listeners land ON the child. A child that emits no
 * pointer events is therefore a dead trigger, and the bubble never opens. A
 * natively disabled control is exactly that case — the browser suppresses
 * mouse events at a disabled `button`/`input`/`select`/`textarea` and they do
 * not bubble to an ancestor either — so it gets an `inline-block` span to
 * hover instead.
 *
 * WHY IT MUST BE NARROW — this is the whole point of the module. The wrapper
 * is a real box, so IT, not the child, becomes the flex/grid item. Every
 * length the child sized against its parent (`width: 33.33%`, `width: 100%`,
 * `flex`) then resolves against a shrink-to-fit box and the child collapses.
 * Testing "does the child have a prop named `disabled`" is far too wide:
 * `disabled` is an ordinary STYLING prop on styled-components throughout this
 * repo. The reference branch shipped that wide test and it squeezed the
 * settings integrations grid to a fraction of its columns — cards at ~110px
 * instead of ~330px, titles truncated to "Enhetsr…", body text at one word per
 * line (`…/company-setting/exact/widgets/MenuList.tsx`). That defect is the
 * reason this file exists; do not widen the test back out.
 *
 * WHAT DECIDES IS NATIVENESS, NOT CSS. `disabled:pointer-events-none` on a
 * shadcn primitive is merely one FURTHER way to end up inert, never the test —
 * reading the criterion off that class leaves a plain `<textarea disabled>`,
 * which carries no such class, wrongly unmarked and its tooltip dead.
 *
 * `aria-disabled` is deliberately NOT part of the test. It is an ARIA state,
 * not an inert one: an `aria-disabled` element keeps taking pointer events, so
 * it needs no wrapper and must keep its own layout box.
 *
 * NOTE FOR THE `development` READER: antd 5.15.4's own Tooltip does no
 * disabled detection AT ALL (`antd/es/tooltip/index.js` only wraps a
 * non-element / fragment child in a bare `<span>`). So none of this changes
 * anything with the design toggle off — it exists so the shadcn half is not
 * WORSE than antd on a disabled control, without being worse than antd on a
 * styled one.
 *
 * Leaf module on purpose: a seam control that carries the marker must not
 * import `Tooltip` — and through it the whole Radix tooltip — just to be
 * marked (the boot-static rule in AGENTS.md).
 * ------------------------------------------------------------------------- */

const INERT_WHEN_DISABLED = '__whPointerInertWhenDisabled'

/** HTML elements that take a `disabled` attribute. */
const DISABLEABLE_TAGS = new Set([
  'button',
  'fieldset',
  'input',
  'optgroup',
  'option',
  'select',
  'textarea',
])

/**
 * antd's own "this component renders a disableable control" markers, read
 * straight off the component object. antd sets them for its own internal use
 * (`Input.Search` reads `__ANT_BUTTON`, Radio.Group reads `__ANT_RADIO`), and
 * they are the cheapest correct way for this seam to recognise an antd control
 * it did not render itself — with the toggle on, an un-ported antd `Switch` or
 * `Radio` is still a perfectly ordinary tooltip child.
 */
const ANTD_MARKERS = [
  '__ANT_BUTTON',
  '__ANT_SWITCH',
  '__ANT_RADIO',
  '__ANT_CHECKBOX',
] as const

/**
 * Declare that this component renders a NATIVELY DISABLEABLE element
 * (`button`/`input`/`select`/`textarea`/`fieldset`/`optgroup`/`option`) as the
 * surface a user points at — so while `disabled` it emits no mouse events at
 * all and a tooltip on it needs the wrapper. Nativeness is the criterion, not
 * any CSS the component happens to carry. Mark nothing else: every marked
 * component gives up its own layout box when disabled.
 *
 * The flag is a plain own property, so `styled(Marked)` keeps it
 * (styled-components hoists non-React statics). A hand-written forwarding
 * wrapper does not — mark it too if it forwards `disabled` to a marked
 * control.
 */
export const markPointerInertWhenDisabled = <T>(component: T): T => {
  Object.assign(component as object, { [INERT_WHEN_DISABLED]: true })
  return component
}

type StyledLike = Record<string, unknown> & { target?: unknown }
type ElementType = string | (React.ComponentType<unknown> & StyledLike)

/**
 * `styled.button` renders a native button but its element type is the styled
 * component, so unwrap styled-components' `target` to see the tag underneath.
 * (`styled(Marked)` needs no unwrapping — the mark is hoisted onto it.)
 */
const resolveHostType = (type: ElementType): ElementType => {
  let current = type
  for (let i = 0; i < 5 && typeof current !== 'string'; i += 1) {
    const { target } = current ?? {}
    if (!target) break
    current = target as ElementType
  }
  return current
}

/** True when a tooltip trigger child would be a dead trigger without a wrapper. */
export const needsDisabledTriggerWrapper = (node: React.ReactNode): boolean => {
  if (!React.isValidElement(node)) return false

  const { disabled } = node.props as { disabled?: unknown }
  if (!disabled) return false

  const type = node.type as ElementType
  if (typeof type !== 'string') {
    const marked = type as Record<string, unknown>
    if (marked?.[INERT_WHEN_DISABLED] === true) return true
    if (ANTD_MARKERS.some((marker) => marked?.[marker] === true)) return true
  }

  const host = resolveHostType(type)
  return typeof host === 'string' && DISABLEABLE_TAGS.has(host)
}
